import { ReactNode } from 'react';
import { CustomFilterProps } from 'ag-grid-react';

/**
 * The base filter model. Used for create filter models
 * @template V - value of model
 * @template N - name of the filter (just use with typeof, like 'typeof filterModelName')
 */
export type BaseFilterModel<V, N extends string> = {
  /**
   * The name of the field for filtering in the server-side API
   */
  filterName: string;
  /**
   * The name of the filter model.
   * In most cases used for matching initial state with filter
   */
  filterModelName: N;
  /**
   * The value type of the filter
   */
  value: V;
};

type ModelFactoryArgs<V> = {
  value: V;
  filterName: string;
};

type ModelFactory<M, N extends string> =
  M extends BaseFilterModel<infer V, N>
    ? (params: ModelFactoryArgs<V>) => M
    : never;

export type FilterComponentProps<M, C = void> = C extends void
  ? Pick<
      CustomFilterProps<unknown, unknown, M>,
      'column' | 'model' | 'onModelChange'
    >
  : Pick<
      CustomFilterProps<unknown, unknown, M>,
      'column' | 'model' | 'onModelChange'
    > & { filterParams: C };

// Component whis is used to create rendered filter
export type FilterComponent<M, C = void> = (
  props: FilterComponentProps<M, C>,
) => ReactNode;

type FilterObject<M, P = void> =
  M extends BaseFilterModel<infer V, infer N>
    ? {
        modelName: N;
        modelFactory: ModelFactory<M, N>;
        component: FilterComponent<BaseFilterModel<V, N>, P>;
      }
    : never;

export type BaseFilterParams = {
  filterName: string;
};

type CreateDataGridFilter<M, P = void> = P extends void
  ? () => FilterObject<M, P>
  : (params: P) => FilterObject<M>;

/**
 * Base filter component for columns
 *
 * @typeParam Model - model type of the filter model
 * @typeParam Params - filter initialization parameters
 */
export type DataGridFilter<
  Model,
  Params extends BaseFilterParams = BaseFilterParams,
> = ReturnType<CreateDataGridFilter<Model, Params>>['component'] & {
  filterName: Params['filterName'];
  modelName: ReturnType<CreateDataGridFilter<Model, Params>>['modelName'];
  modelFactory: ReturnType<CreateDataGridFilter<Model, Params>>['modelFactory'];
};

/**
 * Creates a data grid filter using the provided initialization function.
 *
 * @typeParam Model - model type of the filter model
 * @typeParam Params - filter initialization parameters
 */
export function createDataGridFilter<Model, Params extends BaseFilterParams>(
  initFn: CreateDataGridFilter<Model, Params>,
) {
  return (params: Params): DataGridFilter<Model, Params> => {
    const filter = initFn(params);
    const FilterComponent = filter.component;

    // We realy need this 👇?
    // const filterParams = Object.assign({}, params, {
    //   modelType: filter.modelName,
    //   modelCreator: filter.modelFactory,
    // });

    const ModelComponent = Object.assign(FilterComponent, {
      filterName: params.filterName,
      modelName: filter.modelName,
      modelFactory: filter.modelFactory,
    });

    return ModelComponent;
  };
}

// export type DataGridFilter = ReturnType<
//   ReturnType<typeof createDataGridFilter>
// >;

/**
 * A type helper function that returns the provided filter component.
 *
 * @template M - The model of the filter
 * @template C - The additional props for the filter component
 * @param {FilterComponent<M, C>} component - the filter component to be used
 * @return {FilterComponent<M, C>} the provided filter component
 */
export function createFilterComponent<const M, C = void>(
  component: FilterComponent<M, C>,
) {
  return component;
}
